/**
 * Upload status for the Library Hub — pure helpers behind HubUploadStatus.
 *
 * Two sources, kept apart on purpose:
 *   - the live upload: this task pane's own queue (``UploadSlot[]``), which
 *     only exists while the pane that started it stays open;
 *   - the server's record of each Upload action (``UploadBatch``), written
 *     per file by the upload route, which survives the pane closing and is
 *     the one to trust for "how many went in".
 *
 * Mirrors asthra-v4-web/src/libraries/upload-status.ts, cut down for the
 * narrow task pane.
 */

import type { UploadBatch, UploadFileState, UploadProblem } from "../../api/client";
import type { DocumentRow, UploadSlot } from "./types";

/** What this pane's queue is doing for one library. */
export interface LiveUpload {
  total: number;
  sent: number;
  sending: number;
  waiting: number;
  failed: number;
  /** Nothing left to send or sending. */
  finished: boolean;
}

export function liveUpload(slots: readonly UploadSlot[]): LiveUpload | null {
  if (slots.length === 0) return null;
  let sent = 0;
  let sending = 0;
  let waiting = 0;
  let failed = 0;
  for (const slot of slots) {
    if (slot.status === "uploaded") sent++;
    else if (slot.status === "uploading") sending++;
    else if (slot.status === "waiting") waiting++;
    else failed++;
  }
  return { total: slots.length, sent, sending, waiting, failed, finished: sending === 0 && waiting === 0 };
}

export function countLabel(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}

/** Still being sent, by this pane or another: worth polling. */
export function batchStillMoving(batch: UploadBatch): boolean {
  return batch.counts.waiting > 0 && !batch.stopped;
}

export const PROBLEM_LABEL: Record<UploadProblem["state"], string> = {
  waiting: "Never sent",
  unconfirmed: "Not confirmed",
  failed: "Failed",
  rejected: "Not accepted",
  duplicate: "Already in library",
};

/** "3,212 of 3,300 went in. 40 failed, 12 already in the library, 36 waiting." */
export function batchSentence(batch: UploadBatch): string {
  const c = batch.counts;
  const head = `${c.uploaded.toLocaleString("en-US")} of ${countLabel(batch.total_files, "file")} went in.`;
  const parts: string[] = [];
  if (c.failed > 0) parts.push(`${c.failed.toLocaleString("en-US")} failed`);
  if (c.unconfirmed > 0) parts.push(`${c.unconfirmed.toLocaleString("en-US")} not confirmed`);
  if (c.rejected > 0) parts.push(`${c.rejected.toLocaleString("en-US")} not accepted`);
  if (c.duplicate > 0) parts.push(`${c.duplicate.toLocaleString("en-US")} already in the library`);
  if (c.skipped > 0) parts.push(`${c.skipped.toLocaleString("en-US")} taken out`);
  if (c.waiting > 0) parts.push(`${c.waiting.toLocaleString("en-US")} ${batch.stopped ? "never sent" : "waiting"}`);
  return parts.length === 0 ? head : `${head} ${capitalise(parts.join(", "))}.`;
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Files of a batch that did not go in, counting the ones the server leaves
 *  out of ``problems`` (waiting files of a batch still moving). */
export function problemCount(batch: UploadBatch): number {
  const c = batch.counts;
  return c.failed + c.unconfirmed + c.rejected + c.duplicate + (batch.stopped ? c.waiting : 0);
}

export interface BatchParsing {
  parsed: number;
  parsing: number;
  failed: number;
  /** Went in, but no longer in the library. */
  removed: number;
}

/** How the files a batch put in are getting on, read from the library rows. */
export function batchParsing(batch: UploadBatch, rows: readonly DocumentRow[]): BatchParsing {
  const byId = new Map<string, DocumentRow>();
  for (const row of rows) if (row.doc_id) byId.set(row.doc_id, row);
  const tally: BatchParsing = { parsed: 0, parsing: 0, failed: 0, removed: 0 };
  for (const id of batch.uploaded_document_ids) {
    const row = byId.get(id);
    if (!row) tally.removed++;
    else if (row.processing_status === "failed_processing") tally.failed++;
    else if (row.processing_status === "indexing" || row.processing_status === "uploading") tally.parsing++;
    else tally.parsed++;
  }
  return tally;
}

export function batchParsingSentence(p: BatchParsing): string | null {
  if (p.parsed + p.parsing + p.failed === 0) return null;
  const parts = [`${p.parsed.toLocaleString("en-US")} parsed`];
  if (p.parsing > 0) parts.push(`${p.parsing.toLocaleString("en-US")} still parsing`);
  if (p.failed > 0) parts.push(`${p.failed.toLocaleString("en-US")} couldn't be parsed`);
  return `${capitalise(parts.join(", "))}.`;
}

/** Where the library's documents stand, whenever and however they went in. */
export function libraryTally(rows: readonly DocumentRow[]): BatchParsing & { documents: number } {
  const tally = { documents: 0, parsed: 0, parsing: 0, failed: 0, removed: 0 };
  for (const row of rows) {
    if (!row.doc_id) continue;
    tally.documents++;
    if (row.processing_status === "failed_processing") tally.failed++;
    else if (row.processing_status === "indexing" || row.processing_status === "uploading") tally.parsing++;
    else tally.parsed++;
  }
  return tally;
}

/** "14 Oct, 09:14" in the reader's own time zone. */
export function batchWhen(iso: string | null): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** One CSV field, quoted, with a leading formula character neutralised so a
 *  spreadsheet never runs a file name as a formula. */
export function csvField(value: string): string {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return `"${safe.replace(/"/g, '""')}"`;
}

const STATE_FOR_REPORT: Record<UploadFileState, string> = {
  waiting: "Never sent",
  uploaded: "Uploaded",
  unconfirmed: "Not confirmed",
  failed: "Failed",
  rejected: "Not accepted",
  duplicate: "Already in library",
  skipped: "Taken out",
};

/** The files of a batch that didn't go in, as a CSV a person can work through. */
export function batchReportCsv(batch: UploadBatch): string {
  const lines = [["File", "Folder", "Status", "Reason"].map(csvField).join(",")];
  for (const p of batch.problems) {
    lines.push([p.name, p.folder, STATE_FOR_REPORT[p.state], p.message ?? ""].map(csvField).join(","));
  }
  return `${lines.join("\r\n")}\r\n`;
}

export function reportFileName(libraryName: string, batch: UploadBatch): string {
  const day = (batch.started_at ?? "").slice(0, 10) || "upload";
  const slug = libraryName.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "") || "library";
  return `${slug}-upload-${day}-not-uploaded.csv`;
}
